import { v4 as uuidv4 } from "uuid";

export function browserCanPlay(mime: string): boolean {
  const v = document.createElement("video");
  // Try with common codec strings when useful:
  if (mime === "video/mp4") {
    return !!(
      v.canPlayType('video/mp4; codecs="avc1.42E01E, mp4a.40.2"') ||
      v.canPlayType("video/mp4")
    );
  }
  if (mime === "video/webm") {
    return !!(
      v.canPlayType('video/webm; codecs="vp9,opus"') ||
      v.canPlayType('video/webm; codecs="vp8,vorbis"') ||
      v.canPlayType("video/webm")
    );
  }
  return !!v.canPlayType(mime);
}

export async function sniffVideoMime(
  blob: Blob,
  fileId: string
): Promise<string | null> {
  // First try extension hints
  const id = (fileId || "").toLowerCase();
  if (id.endsWith(".webm") || id.includes("webm")) return "video/webm";
  if (id.endsWith(".mp4") || id.includes("mp4")) return "video/mp4";

  // Then sniff first bytes
  try {
    const buf = new Uint8Array(await blob.slice(0, 64).arrayBuffer());
    const asStr = Array.from(buf)
      .map((c) => String.fromCharCode(c))
      .join("");
    // MP4: 'ftyp' box
    if (asStr.includes("ftyp")) return "video/mp4";
    // WebM (EBML) header 1A 45 DF A3 or "webm" in DocType
    const ebml =
      buf[0] === 0x1a && buf[1] === 0x45 && buf[2] === 0xdf && buf[3] === 0xa3;
    if (ebml || asStr.toLowerCase().includes("webm")) return "video/webm";
    return null;
  } catch {
    return null;
  }
}
export async function ensurePlayableVideoUrl(
  dataUrl: string,
  fileId: string,
  pushToast: (level: "error" | "warning", message: string) => void
): Promise<string> {
  // Already http(s) or blob or correct data:video/* → return as-is
  if (/^(https?:|blob:)/i.test(dataUrl) || /^data:video\//i.test(dataUrl))
    return dataUrl;

  // Some backends send "data:application/octet-stream" or "data:binary/octet-stream"
  const isOctet =
    /^data:(application|binary)\/octet-stream;base64,/i.test(dataUrl) ||
    /^data:[^;]+;base64,/i.test(dataUrl); // catch unknown types too

  if (!/^data:/i.test(dataUrl)) {
    // It might be *just* base64 without a prefix. Wrap as octet-stream for now.
    dataUrl = `data:application/octet-stream;base64,${dataUrl}`;
  }

  if (!isOctet) return dataUrl;

  // Fetch bytes, sniff, wrap in correct MIME, create object URL
  const blob = await (await fetch(dataUrl)).blob();
  const mime = (await sniffVideoMime(blob, fileId)) || "video/mp4";
  const playable = browserCanPlay(mime);

  if (!playable) {
    // We can’t transcode in-browser; surface a helpful error and still return a blob URL.
    const warn =
      `Your browser can't play ${mime} (likely missing H.264/HEVC on this build). ` +
      `Provide a WebM (VP8/VP9) or H.264 MP4 idle clip for maximum compatibility.`;
    pushToast("warning", warn);
  }

  const fixed = new Blob([blob], { type: mime });
  return URL.createObjectURL(fixed);
}

export function getUserDeviceId(): string {
  try {
    const key = "hollo_user_device_id";
    let id = localStorage.getItem(key);
    if (!id) {
      id = uuidv4() || `${Date.now()}-${Math.random()}`;
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    return "unknown-device";
  }
}

/**
 * Generates a new device_id for a new chat session and caches it in localStorage.
 * This creates a unique session identifier that persists across page reloads.
 * @returns The new device_id UUID
 */
export function generateNewDeviceId(): string {
  try {
    const key = "hollo_user_device_id";
    const newId = uuidv4() || `${Date.now()}-${Math.random()}`;
    localStorage.setItem(key, newId);
    return newId;
  } catch {
    return `unknown-device-${Date.now()}`;
  }
}

export function formatServerIssueMessage(e: any): string {
  try {
    const code = e?.error_code;
    const data = e?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data && typeof data === "object" && typeof data.message === "string")
      return data.message;
    if (code) return code;
    return "Unexpected server response";
  } catch {
    return "Unexpected server response";
  }
}
